import { Typography, Box, useMediaQuery } from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Section } from 'src/components/section';
import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { theme } from 'src/styles/theme';
import { RoommateForm } from 'src/features/multi-occupancy';
import { usePostAddRoommatesToBasket } from 'src/lib/hooks/use-post-add-roommates-to-basket';
import { selectBasket, updateBasketRoommates, setBasket } from 'src/store/slices/basket-slice';
import { setBasketStorage } from 'src/utils/local-storage-versioning';
import { RoommateFormValues } from 'src/features/multi-occupancy/constants';

import { styles } from './styles';

import type { RoommatesPageProps } from './types';

export const RoommatesPage: FC<RoommatesPageProps> = ({ onGoBack, onFormSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const basket = useSelector(selectBasket);
    const { mutateAsync: addRoommatesToBasket, isPending: isAddingRoommatesToBasket } =
        usePostAddRoommatesToBasket();

    const onSubmit = async (values: RoommateFormValues) => {
        dispatch(updateBasketRoommates(values.roommates));

        if (!basket || !basket.id || !basket.updated_date) return;

        const response = await addRoommatesToBasket({
            basketId: basket.id,
            roommates: values.roommates,
            updatedAt: basket.updated_date,
        });

        dispatch(setBasket(response));
        setBasketStorage(response);

        onFormSubmit();
    };

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    return (
        <Box css={styles.layout}>
            {!isSmallScreen && (
                <Box css={styles.back} onClick={onGoBack}>
                    <BackArrow css={styles.icon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            )}

            <Section header={t('roommates_title')}>
                <Typography variant="body3">{t('roommates_subheader')}</Typography>
            </Section>

            <RoommateForm onFormSubmit={onSubmit} loading={isAddingRoommatesToBasket} />

            {isSmallScreen && (
                <Box css={styles.back} onClick={onGoBack}>
                    <Typography>{t('agreements_back_content')}</Typography>
                </Box>
            )}
        </Box>
    );
};
